/**
 * O Kovu flutuando no canto do painel — o `kovu_mascot.dart`.
 *
 * O relógio dele (as falas, o intervalo ocioso, o pulo) mora no `usarGuardiao`
 * do pacote comum, junto com o gêmeo do celular. Aqui fica só o desenho.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **Preso na janela, e acima da barra.** Lá é um `Positioned` dentro do
 *   `Stack` da tela. Aqui é `position: fixed`, e a barra de navegação é
 *   `sticky` com `z-index: 10` — o Kovu fica em 9 e sobe a altura da barra,
 *   para nunca cobrir um destino nem sumir atrás do rodapé.
 * - **Segurar é segurar, e o botão direito também vale.** Lá o carinho é
 *   `onLongPress`, que num mouse ninguém faz. Aqui o toque longo continua
 *   valendo — de dedo ou de mouse —, e quem tem botão direito chega pelo menu
 *   de contexto. O clique curto abre a conversa, como lá.
 * - **Duas caixas, não uma.** O `Transform.translate` e o `Transform.scale` de
 *   lá são dois; em CSS duas animações no mesmo elemento brigam pela mesma
 *   propriedade `transform`. A de fora flutua, a de dentro pula.
 * - **Corações em CSS, não `AnimatedPositioned`.** Mesma conta: oito, em
 *   círculo de raio 60, sumindo em 800ms.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { traduzir, usarGuardiao, type HumorDaCasa } from '@nosso-sempre/shared';
import { Icone } from './Icone';
import cara from '../imagens/kovu.webp';
import './kovuFlutuante.css';

/** Os oito corações do `_fazerCarinho`, em círculo, como lá. */
const QUANTOS_CORACOES = 8;

/** O `Future.delayed(milliseconds: 1000)` que limpa as partículas. */
const CORACOES_SOMEM = 1_000;

/** Quanto tempo é "segurar". O padrão do `onLongPress` do Flutter. */
const SEGURAR = 500;

export function KovuFlutuante({ humores }: { humores: ReadonlyArray<HumorDaCasa> }) {
  const ir = useNavigate();
  const { fala, pulando, carinho } = usarGuardiao(humores);
  const [rodada, setRodada] = useState(0);

  const somem = useRef<ReturnType<typeof setTimeout> | null>(null);
  const segurando = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Quem segurou já recebeu o carinho: o clique que vem depois de soltar não
     pode levar a pessoa para outra tela sem querer. */
  const foiCarinho = useRef(false);

  useEffect(
    () => () => {
      if (somem.current) clearTimeout(somem.current);
      if (segurando.current) clearTimeout(segurando.current);
    },
    [],
  );

  /* A rodada é a chave dos corações: sem ela o segundo carinho reaproveita os
     mesmos nós e a animação não recomeça. */
  const fazerCarinho = () => {
    carinho();
    setRodada((n) => n + 1);
    if (somem.current) clearTimeout(somem.current);
    somem.current = setTimeout(() => setRodada(0), CORACOES_SOMEM);
  };

  const segurar = () => {
    foiCarinho.current = false;
    segurando.current = setTimeout(() => {
      foiCarinho.current = true;
      fazerCarinho();
    }, SEGURAR);
  };

  const soltar = () => {
    if (segurando.current) clearTimeout(segurando.current);
  };

  return (
    <div className="kovu-flutua">
      {fala ? (
        <p className="kovu-balao" role="status">
          {fala}
        </p>
      ) : null}

      {rodada > 0
        ? Array.from({ length: QUANTOS_CORACOES }, (_, i) => (
            <span
              key={`${rodada}-${i}`}
              className="kovu-coracao"
              aria-hidden="true"
              style={{ ['--giro' as string]: `${(360 / QUANTOS_CORACOES) * i}deg` }}
            >
              <Icone nome="coracao" tamanho={12} preenchido />
            </span>
          ))
        : null}

      <button
        type="button"
        className={`kovu-botao${pulando ? ' pula' : ''}`}
        title={traduzir('Falar com o Kovu')}
        aria-label={traduzir('Abrir a conversa com o Kovu')}
        onPointerDown={segurar}
        onPointerUp={soltar}
        onPointerLeave={soltar}
        onPointerCancel={soltar}
        onContextMenu={(ev) => {
          ev.preventDefault();
          fazerCarinho();
        }}
        onClick={() => {
          if (foiCarinho.current) {
            foiCarinho.current = false;
            return;
          }
          ir('/kovu');
        }}
      >
        <img className="kovu-avatar" src={cara} alt="" loading="lazy" decoding="async" />
      </button>
    </div>
  );
}
